import { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react';

const Toast = ({ 
  message, 
  type = 'error', 
  isVisible = false, 
  onClose, 
  duration = 5000,
  position = 'top-right',
  closable = true 
}) => {
  const [show, setShow] = useState(false);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShow(true);
      // Trigger animation after component mounts
      setTimeout(() => setAnimate(true), 10);
      
      // Auto close after duration
      if (duration > 0) {
        const timer = setTimeout(() => {
          handleClose();
        }, duration);
        
        return () => clearTimeout(timer);
      }
    } else {
      handleClose();
    }
  }, [isVisible, duration]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(() => {
      setShow(false);
      if (onClose) onClose();
    }, 300); // Match animation duration
  };

  const getToastConfig = () => {
    const configs = {
      success: {
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        textColor: 'text-green-800',
        iconColor: 'text-green-600',
        icon: CheckCircle,
        title: 'Succès'
      },
      error: {
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        textColor: 'text-red-800',
        iconColor: 'text-red-600',
        icon: AlertCircle,
        title: 'Erreur'
      },
      warning: {
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        textColor: 'text-yellow-800',
        iconColor: 'text-yellow-600',
        icon: AlertTriangle,
        title: 'Attention'
      },
      info: {
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        textColor: 'text-blue-800',
        iconColor: 'text-blue-600',
        icon: Info,
        title: 'Information'
      }
    };
    return configs[type] || configs.error;
  };

  const getPositionClasses = () => {
    const positions = {
      'top-right': 'top-4 right-4',
      'top-left': 'top-4 left-4',
      'top-center': 'top-4 left-1/2 transform -translate-x-1/2',
      'bottom-right': 'bottom-4 right-4',
      'bottom-left': 'bottom-4 left-4',
      'bottom-center': 'bottom-4 left-1/2 transform -translate-x-1/2',
    };
    return positions[position] || positions['top-right'];
  };

  const getAnimationClasses = () => {
    const isRight = position.includes('right');
    const isLeft = position.includes('left');
    const isCenter = position.includes('center');
    
    if (isRight) {
      return animate ? 'translate-x-0' : 'translate-x-full';
    } else if (isLeft) {
      return animate ? 'translate-x-0' : '-translate-x-full';
    } else if (isCenter) {
      return animate ? 'translate-y-0' : '-translate-y-full';
    }
    return animate ? 'translate-x-0' : 'translate-x-full';
  };

  if (!show) return null;

  const config = getToastConfig();
  const IconComponent = config.icon;

  return (
    <div className={`fixed z-50 ${getPositionClasses()}`}>
      <div 
        className={`
          ${config.bgColor} ${config.borderColor} ${config.textColor}
          border rounded-lg shadow-lg p-4 max-w-sm w-full
          transform transition-all duration-300 ease-out
          ${getAnimationClasses()}
          hover:shadow-xl
        `}
      >
        <div className="flex items-start space-x-3">
          <div className={`${config.iconColor} flex-shrink-0 mt-0.5`}>
            <IconComponent className="h-5 w-5" />
          </div>
          
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium mb-1">
              {config.title}
            </p>
            <p className="text-sm opacity-90 leading-relaxed">
              {message}
            </p>
          </div>
          
          {closable && (
            <button
              onClick={handleClose}
              className={`${config.iconColor} hover:opacity-70 transition-opacity flex-shrink-0`}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Toast;